/**
 * GDPR Utilities for consent validation and enforcement
 */

import { CookieConsent, ConsentCategory } from '../contexts/CookieConsentContext';
import config from '../config';

// Consent validation and enforcement helpers
export class GDPREnforcement {
  
  /**
   * Check if a specific script/service should be loaded based on consent
   */
  static canLoadService(category: ConsentCategory, consent: CookieConsent | null): boolean {
    if (!consent) {
      // Before consent is given, only necessary services allowed
      return category === 'necessary';
    }
    
    return consent[category] === true;
  }

  /**
   * Block/unblock analytics services based on consent
   */
  static enforceAnalyticsConsent(hasAnalyticsConsent: boolean): void {
    if (hasAnalyticsConsent) {
      // Enable analytics if consent given
      GDPREnforcement.enableGoogleAnalytics();
    } else {
      // Disable analytics if no consent
      GDPREnforcement.disableGoogleAnalytics();
    }
  }

  /**
   * Enable Google Analytics with GDPR-compliant settings
   */
  static enableGoogleAnalytics(): void {
    // Only load if not already loaded
    if (window.gtag || document.querySelector('[data-gtag-loaded]')) {
      return;
    }

    try {
      // Create script tag
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${config.analytics.googleAnalytics.measurementId}`;
      script.async = true;
      script.setAttribute('data-gtag-loaded', 'true');
      document.head.appendChild(script);

      // Initialize gtag
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer!.push(arguments);
      };

      window.gtag('js', new Date());
      window.gtag('config', config.analytics.googleAnalytics.measurementId, {
        // GDPR compliant settings
        anonymize_ip: true,                    // Required for GDPR
        allow_google_signals: false,           // Disable advertising features  
        allow_ad_personalization_signals: false, // Disable ad personalization
        cookie_flags: 'SameSite=Strict;Secure', // Secure cookie settings
        cookie_expires: 60 * 60 * 24 * 30,     // 30 days retention
        custom_map: {},                        // No custom dimensions initially
        send_page_view: false                  // We'll manually control page views
      });
    } catch (error) {
      console.error('❌ Error enabling Google Analytics:', error);
    }
  }

  /**
   * Disable Google Analytics and clean up
   */
  static disableGoogleAnalytics(): void {
    try {
      // Remove GA script
      const gaScript = document.querySelector('[data-gtag-loaded]');
      if (gaScript) {
        gaScript.remove();
      }

      // Clear gtag function
      if (window.gtag) {
        delete window.gtag;
      }

      // Clear dataLayer
      window.dataLayer = [];

      // Clear GA cookies
      GDPREnforcement.clearGoogleAnalyticsCookies();

      // Google Analytics disabled and cleaned up
    } catch (error) {
      console.error('❌ Error disabling Google Analytics:', error);
    }
  }

  /**
   * Clear Google Analytics cookies
   */
  static clearGoogleAnalyticsCookies(): void {
    const gaCookies = [
      '_ga',
      '_ga_' + config.analytics.googleAnalytics.measurementId.replace('G-', ''),
      '_gat',
      '_gid'
    ];

    gaCookies.forEach(cookieName => {
      // Clear cookie for current domain
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
      // Clear cookie for parent domain (if subdomain)
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname.split('.').slice(-2).join('.')}`;
    });
  }

  /**
   * Validate consent object structure
   */
  static validateConsent(consent: any): consent is CookieConsent {
    if (!consent || typeof consent !== 'object') return false;
    
    const requiredFields = ['necessary', 'analytics', 'marketing', 'functional', 'consentDate', 'version'];
    const hasAllFields = requiredFields.every(field => field in consent);
    
    if (!hasAllFields) return false;
    
    // Validate types
    if (consent.necessary !== true) return false;
    if (typeof consent.analytics !== 'boolean') return false;
    if (typeof consent.marketing !== 'boolean') return false;
    if (typeof consent.functional !== 'boolean') return false;
    if (typeof consent.version !== 'string') return false;
    
    // Validate date
    const consentDate = new Date(consent.consentDate);
    if (isNaN(consentDate.getTime())) return false;
    
    return true;
  }

  /**
   * Get consent summary for logging/audit
   */
  static getConsentAuditLog(consent: CookieConsent | null): object {
    return {
      timestamp: new Date().toISOString(),
      consent: consent ? {
        necessary: consent.necessary,
        analytics: consent.analytics,
        marketing: consent.marketing,
        functional: consent.functional,
        version: consent.version,
        consentDate: consent.consentDate
      } : null,
      userAgent: navigator.userAgent,
      url: window.location.href
    };
  }

  /**
   * Check if consent needs renewal (e.g., after 1 year)
   */
  static needsConsentRenewal(consent: CookieConsent | null, maxAgeMonths: number = 12): boolean {
    if (!consent) return true;
    
    const consentDate = new Date(consent.consentDate);
    const now = new Date();
    const monthsDiff = (now.getTime() - consentDate.getTime()) / (1000 * 60 * 60 * 24 * 30);
    
    return monthsDiff > maxAgeMonths;
  }

  /**
   * Get list of third-party services based on consent
   */
  static getActiveThirdPartyServices(consent: CookieConsent | null): string[] {
    const services: string[] = [];
    
    if (!consent) return services;
    
    if (consent.analytics) {
      services.push('Google Analytics');
    }
    
    if (consent.marketing) {
      services.push('Marketing Pixels');
      services.push('Retargeting');
    }
    
    if (consent.functional) {
      services.push('Chat Widget');
      services.push('Video Embeds');
    }
    
    return services;
  }
}

// Type extensions for window object
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

export default GDPREnforcement;